import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { SAMPLE_BILL } from '../utils/receiptPreview';
import { composeReceipt } from '../utils/composeReceipt';
import { ReceiptBlocks } from './ReceiptBlocks';
import { receiptColumns } from '../data/billTemplates';

// One scale for every roll, so the sheets are honest about their relative
// widths: a 112mm design really is about twice a 58mm one on screen.
const PX_PER_MM = 3.5;
const SHEET_PADDING = 24; // px, the 12px gutter on each side of the text
// A4 at 3.5px/mm would be 735px, far past any panel it is previewed in. Capped,
// so the biggest sheet still fits and the rolls keep their true proportions.
const MAX_SHEET_PX = 460;

/**
 * A receipt drawn as the slip of paper it becomes.
 *
 * Three screens show a design — the editor, the template gallery and the eye
 * preview on the Branches tab — and each had drawn its own `<pre>` in a grey
 * box. That reads as a block of code, when the thing being previewed is what a
 * customer is handed. One sheet, used by all three, so they cannot drift.
 *
 * The width is the width of the roll. A 58mm design shown at 80mm looks like it
 * fits when it does not, which is the whole reason the preview exists.
 */
export const ReceiptSheet = ({ org, settings, caption = true }) => {
  const cols = receiptColumns(settings.paperWidth, settings.charsPerLine);
  // Composed rather than rendered to a string: a flat string loses bold, big
  // and alignment, so a design's own choices never showed on its preview.
  const { blocks } = composeReceipt(org, SAMPLE_BILL, settings);
  const width = Math.min(MAX_SHEET_PX, Math.round((settings.paperWidth || 80) * PX_PER_MM));

  // The sheet is measured rather than assumed. It wants `width` pixels but takes
  // whatever the column it sits in allows — a 112mm design is 392px wide and the
  // editor's preview rail is narrower than that — so the type must be sized from
  // the room actually available.
  const sheetRef = useRef(null);
  const [boxWidth, setBoxWidth] = useState(width);
  useEffect(() => {
    const el = sheetRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      if (w > 0) setBoxWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A full line always fits, rather than the type being left at a fixed size and
  // allowed to overflow. At a fixed 10.5px a 112mm design ran past the edge and
  // took the amounts with it — every total on the preview was cut off, which is
  // the one thing a shop looks at. 0.6em is a monospace glyph's advance width.
  // Floored as well as capped: a sheet measured mid-layout can report a width of
  // zero, and a negative font size renders nothing at all.
  const fontSize = Math.max(5, Math.min(11, (boxWidth - SHEET_PADDING) / (cols.width * 0.6)));

  return (
    <div className="bg-slate-100/70 rounded-lg py-5 px-3 flex flex-col items-center gap-2.5">
      <div
        ref={sheetRef}
        className="bg-white shadow-[0_1px_2px_rgba(15,23,42,0.10),0_8px_24px_-12px_rgba(15,23,42,0.25)] w-full"
        style={{ maxWidth: width }}
      >
        {/* Whitespace is significant — the composer has already padded every row
            to the character width of the paper — so the face must be monospace
            and the text must not be allowed to collapse or wrap. */}
        <div className="text-slate-800 px-3 py-4">
          <ReceiptBlocks blocks={blocks} width={cols.width} fontSize={Number(fontSize.toFixed(2))} />
        </div>
      </div>

      {caption && (
        <p className="text-[10px] font-medium text-slate-400">
          {settings.paperWidth} mm · {cols.width} characters per line
        </p>
      )}
    </div>
  );
};

ReceiptSheet.propTypes = {
  org: PropTypes.object.isRequired,
  settings: PropTypes.object.isRequired,
  caption: PropTypes.bool,
};
